/* eslint-disable max-len */
import React, { useContext, useState } from 'react';
import {
  bool as boolType,
  any,
  oneOf,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import { Link } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import CollectionsTitle from './CollectionsTitle';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import VisualTabs from './VisualTabs';
import CollectionsImage from './CollectionsImage';
import { RecsProductPod } from './RecsProductPod';
import '../../styles/thd-recs-containers.scss';

const Wrapper = ({ children }) => children;

Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
  })
}, RecsProductPod);
const CollectionCarousel = (props) => {

  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    data,
    experienceName,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    subtitle,
    isCategoryTab, // Displaying the category tabs
    hideATC,
    hideFavorites,
    hideBadge,
    membershipInformation,
    zipCode,
    slidesPer,
    keepAddToCart,
    hideSwatches,
    hideCarouselArrows,
    analyticsAnchorProductSku,
    parent,
    brandTitleMaxLine,
    hideRating
  } = props;

  let hideAddToCart;

  let recsCollectionTabCount = useConfigService('fs:recsCollectionTabCount') || 4;

  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null
  };

  const { products, metadata, categories } = data || {};

  const getClassNames = () => {
    if (!isMobile && experienceName) {
      return classNames({ 'sui-col-span-2': !experienceName }, { 'sui-col-span-4': experienceName });
    } if (isMobile && experienceName) {
      return classNames({ 'sui-col-span-12': experienceName });
    } return classNames({ 'sui-col-span-2': (metadata?.collectionImageUrl && isCategoryTab) }, { 'sui-col-span-12': (metadata?.collectionImageUrl && !isCategoryTab) });
  };

  const classes = classNames('sui-grid', 'sui-gap-2', 'sui-grid-cols-1', { 'sui-grid-cols-3': !experienceName && !isMobile }, { 'sui-grid-cols-5': experienceName && !isMobile });

  const colSpanClasses = getClassNames();

  let title = !experienceName ? metadata?.collectionName : 'Make It Match';
  let collectionLink = experienceName ? 'Shop More In This Style' : metadata?.collectionName;

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts;
  let recommendationTab = (metadata?.collectionImageUrl || isCategoryTab) && !experienceName;

  if (isCategorySelected) {
    displayProducts = filtered;
  } else {
    displayProducts = products;
  }

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: recommendationTab ? 4 : 5,
        slidesPerGroup: recommendationTab ? 4 : 5
      },
      xl: {
        slidesPerView: recommendationTab ? 4 : 5,
        slidesPerGroup: recommendationTab ? 4 : 5
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: recommendationTab ? 1 : slidesPer,
        slidesPerGroup: recommendationTab ? 1 : slidesPer
      },
      xl: {
        slidesPerView: recommendationTab ? 1 : slidesPer,
        slidesPerGroup: recommendationTab ? 1 : slidesPer
      }
    });
  }

  let placeholderTxtLines;
  if (!isMobile && isCategoryTab) {
    placeholderTxtLines = 19;
  } else {
    placeholderTxtLines = 10;
  }

  return (
    <>
      {showLoading && loading && (
        <LoadingPlaceholder showMoreLines={(isCategoryTab)} txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}

      {displayProducts?.length > 0 && (
        <>
          {metadata?.collectionName && (
            <CarouselHeader title={title} subtitle={subtitle} />
          )}

          {/* Collection link for hd-home */}
          {(metadata?.collectionPlpUrl && experienceName) && (
            <div className="sui-pt-5 sui-pb-5 sui-text-sm sui-text-info">
              <Link
                href={metadata?.collectionPlpUrl}
                target={isMobile ? '_self' : '_blank'}
                rel="noreferrer"
                data-testid="tooltip-product"
              > {collectionLink}
              </Link>
            </div>
          )}

          {/* collection title enabled for GM */}
          {(isCategoryTab && !experienceName) && (
            <CollectionsTitle
              metadata={metadata}
              productIdentifier={products[0]?.product?.identifiers}
              isMobile={isMobile}
              isCategoryTab={isCategoryTab}
              colSpanClasses={colSpanClasses}
              recsCollectionTabCount={recsCollectionTabCount}
              categories={categories}
              pullFilteredData={pullFilteredData}
              products={products}
              experienceName={experienceName}
            />
          )}

          {/* category tab enable for hd-home */}
          {!isMobile && isCategoryTab && experienceName && (
            <div className={colSpanClasses}>
              {isCategoryTab && categories?.length > 0 && categories?.length >= recsCollectionTabCount && (
                // eslint-disable-next-line max-len
                <VisualTabs products={products} categories={categories} filterItemIds={pullFilteredData} experienceName={experienceName} />
              )}
            </div>
          )}

          {/* category tab and title enable for hd-home */}
          <div className={classNames(classes)}>
            {(() => {
              if (metadata?.collectionImageUrl || isCategoryTab) {
                return (
                  <div>
                    {experienceName && isCategoryTab && (
                      <CollectionsTitle metadata={metadata} productIdentifier={products[0]?.product?.identifiers} isCategoryTab={isCategoryTab} isMobile={isMobile} experienceName={experienceName} />
                    )}
                    <CollectionsImage metadata={metadata} firstProductMedia={products[0]?.product?.media} isMobile={isMobile} experienceName={experienceName} />
                  </div>
                );
              }
              return null;
            })()}
            <div className={colSpanClasses}>
              <QueryProvider
                dataSource={scheme}
                cacheKey={`${scheme}-recommendations-carousel`}
                defaultVariables={defaultVariables}
              >
                <Carousel
                  breakpoints={breakpoints}
                  hideCarouselArrows={hideCarouselArrows}
                  disableMargin
                >
                  {displayProducts.map((dynamicProduct, idx) => {
                    const { product } = dynamicProduct;
                    const { itemId } = product;
                    const { preferredPriceFlag } = product.pricing;

                    return (
                      <Wrapper key={idx}>
                        <RecsProductPod
                          itemId={itemId}
                          key={itemId}
                          position={idx}
                          storeId={storeId}
                          scheme={scheme}
                          anchorProduct={analyticsAnchorProductSku}
                          parent={parent}
                          hideSwatches={hideSwatches}
                          hideFavorites={hideFavorites}
                          hideBadge={hideBadge}
                          hideATC={hideATC}
                          hideRating={hideRating}
                          preferredPriceFlag={preferredPriceFlag}
                          brandTitleMaxLine={brandTitleMaxLine}
                        />
                      </Wrapper>
                    );
                  }
                  )}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, RecsProductPod.dataModel.product);

const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});
CollectionCarousel.dataModel = extend(Wrapper, wrappedProduct);

CollectionCarousel.displayName = 'CollectionCarousel';

CollectionCarousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  keepAddToCart: boolType,
  isCategoryTab: boolType,
  brandTitleMaxLine: oneOf([2, 3, null]),
  hideSwatches: boolType,
  hideCarouselArrows: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType
};

CollectionCarousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideBadge: false,
  hideATC: false,
  hideFavorites: true,
  keepAddToCart: null,
  isCategoryTab: false,
  brandTitleMaxLine: 4,
  hideSwatches: true,
  hideCarouselArrows: false,
  analyticsAnchorProductSku: '',
  hideRating: false
};

export { CollectionCarousel };
